/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import * as actionType from './actionTypes';

export const fetchNodesIfRequired = (skillId, selectedNode = false) => ({
  type: actionType.FETCH_NODES_IF_REQUIRED,
  skillId,
  selectedNode
});

export const fetchNodes = (skillId: string, selectedNode = false) => ({
  type: actionType.FETCH_NODES,
  skillId,
  selectedNode
});

export const refreshNodes = skillId => ({
  type: actionType.REFRESH_NODES,
  skillId
});

export const clearNodes = preserveSelectedNode => ({
  type: actionType.CLEAR_NODES,
  preserveSelectedNode: preserveSelectedNode
});

export const addNodes = newNodes => ({
  type: actionType.ADD_NODES,
  newNodes
});
export const addNodesCache = () => ({
  type: actionType.ADD_NODES_CACHE
});

// ADD NODE
export const addNode = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  newNode: any,
  select: any
) => ({
  type: actionType.ADD_NODE,
  skillId,
  useNewIAUpdatedAccess,
  assistantId,
  newNode,
  select
});

export const addNodeStart = (newNode, select) => ({
  type: actionType.ADD_NODE_START,
  newNode,
  select
});

export const addNodeError = (newNode, select) => ({
  type: actionType.ADD_NODE_ERROR,
  newNode,
  select
});

export const addNodeSuccess = (newNode, select) => ({
  type: actionType.ADD_NODE_SUCCESS,
  newNode,
  select
});

// ADD RESPONSE
export const addResponse = (skillId, newResponse) => ({
  type: actionType.ADD_RESPONSE,
  skillId,
  newResponse
});

export const addResponseStart = newResponse => ({
  type: actionType.ADD_RESPONSE_START,
  newResponse
});

export const addResponseError = newResponse => ({
  type: actionType.ADD_RESPONSE_ERROR,
  newResponse
});

export const addResponseSuccess = newResponse => ({
  type: actionType.ADD_RESPONSE_SUCCESS,
  newResponse
});

// ADD SLOT
export const addSlot = (skillId, newSlot) => ({
  type: actionType.ADD_SLOT,
  skillId,
  newSlot
});

export const addSlotStart = newSlot => ({
  type: actionType.ADD_SLOT_START,
  newSlot
});

export const addSlotError = newSlot => ({
  type: actionType.ADD_SLOT_ERROR,
  newSlot
});

export const addSlotSuccess = (newSlot, inputHandler, focusHandler) => ({
  type: actionType.ADD_SLOT_SUCCESS,
  newSlot,
  inputHandler,
  focusHandler
});

// DELETE NODE
export const deleteNode = (skillId, node, childNodesToPreserve) => ({
  type: actionType.DELETE_NODE,
  skillId,
  node,
  childNodesToPreserve
});

export const deleteNodeStart = node => ({
  type: actionType.DELETE_NODE_START,
  node
});

export const deleteNodeError = node => ({
  type: actionType.DELETE_NODE_ERROR,
  node
});

export const deleteNodeSuccess = (node, childNodesToPreserve) => ({
  type: actionType.DELETE_NODE_SUCCESS,
  node,
  childNodesToPreserve
});

//
export const expandNode = (nodeId, expanded) => ({
  type: actionType.EXPAND_NODE,
  nodeId,
  expanded
});

export const expandPath = (path, expanded) => ({
  type: actionType.EXPAND_PATH,
  path,
  expanded
});

export const selectNode = (nodeId, selected) => ({
  type: actionType.SELECT_NODE,
  nodeId,
  selected
});

export const selectNodes = (nodesWithAncestors, nodesSelected) => ({
  type: actionType.SELECT_NODES,
  nodesWithAncestors,
  nodesSelected
});

export const selectNodeWithError = nodeId => ({
  type: actionType.SELECT_NODES_WITH_ERRORS,
  nodeId
});

// UPDATE NODE
export const updateNode = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  nodeId: string,
  node: any
) => ({
  type: actionType.UPDATE_NODE,
  skillId,
  assistantId,
  useNewIAUpdatedAccess,
  nodeId,
  node
});

export const updateNodeStart = (
  nodeId: string,
  node: any,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => ({
  type: actionType.UPDATE_NODE_START,
  nodeId,
  node,
  useNewIAUpdatedAccess,
  assistantId
});

export const updateNodeError = (nodeId, node) => ({
  type: actionType.UPDATE_NODE_ERROR,
  nodeId,
  node
});

export const updateNodeSuccess = (nodeId, node) => ({
  type: actionType.UPDATE_NODE_SUCCESS,
  nodeId,
  node
});

//
export const updateNodes = updatedNodes => ({
  type: actionType.UPDATE_NODES,
  updatedNodes
});

// UPDATE SLOT
export const updateSlot = (skillId, slot, inputHandler, focusHandler) => ({
  type: actionType.UPDATE_SLOT,
  skillId,
  slot,
  inputHandler,
  focusHandler
});

export const updateSlotStart = (slot, inputHandler, focusHandler) => ({
  type: actionType.UPDATE_SLOT_START,
  slot,
  inputHandler,
  focusHandler
});

export const updateSlotError = (slot, inputHandler, focusHandler) => ({
  type: actionType.UPDATE_SLOT_ERROR,
  slot,
  inputHandler,
  focusHandler
});

export const updateSlotSuccess = (slot, inputHandler, focusHandler) => ({
  type: actionType.UPDATE_SLOT_SUCCESS,
  slot,
  inputHandler,
  focusHandler
});

// UPDATE HANDLERS
export const updateHandlers = (
  skillId,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => ({
  type: actionType.UPDATE_HANDLERS,
  skillId,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
});

// UPDATE SLOT AND HANDLERS
export const updateSlotAndHandlers = (
  skillId,
  slot,
  inputHandler,
  focusHandler,
  otherHandlers
) => ({
  type: actionType.UPDATE_SLOT_AND_HANDLERS,
  skillId,
  slot,
  inputHandler,
  focusHandler,
  otherHandlers
});

// UPDATE SLOT AND HANDLERS
export const updateSlotAndHandlersSuccess = slotAndHandlers => ({
  type: actionType.UPDATE_SLOT_AND_HANDLERS_SUCCESS,
  slotAndHandlers
});

export const updateSlotAndHandlersError = slotAndHandlers => ({
  type: actionType.UPDATE_SLOT_AND_HANDLERS_ERROR,
  slotAndHandlers
});

export const updateHandlersStart = (
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => ({
  type: actionType.UPDATE_HANDLERS_START,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
});

export const updateHandlersError = (
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => ({
  type: actionType.UPDATE_HANDLERS_ERROR,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
});

export const updateHandlersSuccess = (
  nodeId,
  handlerType,
  existingHandlers,
  handlers
) => ({
  type: actionType.UPDATE_HANDLERS_SUCCESS,
  nodeId,
  handlerType,
  existingHandlers,
  handlers
});

// MOVE SUBNODE
export const moveSubnode = (skillId, subnode, updatedSubnode, direction) => ({
  type: actionType.MOVE_SUBNODE,
  skillId,
  subnode,
  updatedSubnode,
  direction
});

export const moveSubnodeStart = (subnode, direction) => ({
  type: actionType.MOVE_SUBNODE_START,
  subnode,
  direction
});

export const moveSubnodeError = (subnode, direction) => ({
  type: actionType.MOVE_SUBNODE_ERROR,
  subnode,
  direction
});

export const moveSubnodeSuccess = (subnode, direction) => ({
  type: actionType.MOVE_SUBNODE_SUCCESS,
  subnode,
  direction
});

// ENABLE SLOTS

export const enableSlots = (
  skillId,
  node,
  enable,
  enablePrompt,
  existingSlots,
  existingPrompt,
  digressOutSlots
) => ({
  type: actionType.ENABLE_SLOTS,
  enablePrompt,
  skillId,
  node,
  enable,
  existingSlots,
  existingPrompt,
  digressOutSlots
});

export const enableSlotsStart = node => ({
  type: actionType.ENABLE_SLOTS_START,
  node
});

export const enableSlotsSuccess = (nodeId, node) => ({
  type: actionType.ENABLE_SLOTS_SUCCESS,
  nodeId: nodeId,
  node
});

// ENABLE MCR
export const enableMcr = (skillId, node, responses, enable) => ({
  type: actionType.ENABLE_MCR,
  skillId,
  node,
  responses,
  enable
});

export const enableMcrStart = (node, enable) => ({
  type: actionType.ENABLE_MCR_START,
  node,
  enable
});

export const enableMcrError = (node, enable) => ({
  type: actionType.ENABLE_MCR_ERROR,
  node,
  enable
});

export const enableMcrSuccess = (node, responses, enable) => ({
  type: actionType.ENABLE_MCR_SUCCESS,
  node,
  responses,
  enable
});

// ENABLE CALLOUT
export const enableCallout = (
  skillId,
  node,
  responses,
  calloutInfo,
  enableMcr
) => ({
  type: actionType.ENABLE_CALLOUT,
  skillId,
  node,
  responses,
  calloutInfo,
  enableMcr
});

export const enableCalloutStart = (node, updatedNode, calloutInfo, action) => ({
  type: actionType.ENABLE_CALLOUT_START,
  node,
  updatedNode,
  calloutInfo,
  action
});

export const enableCalloutError = (node, calloutInfo) => ({
  type: actionType.ENABLE_CALLOUT_ERROR,
  node,
  calloutInfo
});

export const enableCalloutSuccess = (
  node,
  responses,
  newResponses,
  action
) => ({
  type: actionType.ENABLE_CALLOUT_SUCCESS,
  node,
  responses,
  newResponses,
  action
});

// MOVE NODE
export const moveNode = (skillId, sourceNode, targetNode, where) => ({
  type: actionType.MOVE_NODE,
  skillId,
  sourceNode,
  targetNode,
  where
});

export const moveNodeStart = (sourceNode, targetNode, where) => ({
  type: actionType.MOVE_NODE_START,
  sourceNode,
  targetNode,
  where
});

export const moveNodeSuccess = nodeId => ({
  type: actionType.MOVE_NODE_SUCCESS,
  nodeId
});

export const moveNodeError = (sourceNode, undoNode) => ({
  type: actionType.MOVE_NODE_ERROR,
  sourceNode,
  undoNode
});

// DUPLICATE NODE
export const duplicateNode = (skillId, sourceNode, newNode) => ({
  type: actionType.DUPLICATE_NODE,
  skillId,
  sourceNode,
  newNode
});

export const duplicateNodeStart = (sourceNode, newNode, allDuplicates) => ({
  type: actionType.DUPLICATE_NODE_START,
  sourceNode,
  newNode,
  allDuplicates
});

export const duplicateNodeSuccess = nodeId => ({
  type: actionType.DUPLICATE_NODE_SUCCESS,
  nodeId
});

export const duplicateNodeError = newNode => ({
  type: actionType.DUPLICATE_NODE_ERROR,
  newNode
});

export const updateJumpTo = (skillId, updatedJumpTo, noSave) => ({
  type: actionType.UPDATE_JUMP_TO,
  skillId,
  updatedJumpTo,
  noSave
});

export const updateJumpToStart = (updatedJumpTo, noSave) => ({
  type: actionType.UPDATE_JUMP_TO_START,
  updatedJumpTo,
  noSave
});

export const updateJumpToSuccess = node => ({
  type: actionType.UPDATE_JUMP_TO_SUCCESS,
  node
});

export const updateJumpToError = () => ({
  type: actionType.UPDATE_JUMP_TO_ERROR
});
